import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  ErroresBackendForm,
  parsearErrorBackend,
} from '../../core/errores-backend';
import {
  ContactoService,
  MENSAJE_MAX,
} from '../../core/services/contacto.service';
import { validadorEmail } from '../../core/validador-email';
import { Alerta } from '../../shared/alerta/alerta';
import { InputTexto } from '../../shared/input-texto/input-texto';
import { Modal } from '../../shared/modal/modal';

/** Mensaje por control cuando el rechazo lo pone la validación del front. */
const ERROR_LOCAL: Record<string, string> = {
  nombre: 'El nombre es obligatorio',
  email: 'El email es obligatorio y debe ser válido',
  mensaje: 'El mensaje es obligatorio',
};

/**
 * Pantalla de Contacto del portal (FP-40).
 *
 * Portada de `ContactoPage` del backoffice (`src/pages/contacto/`): hasta ahora
 * el ítem `Contacto` del menú saltaba al backoffice (FP-26 / decisión D5), lo que
 * dejaba al visitante en otra aplicación —con otro header, otro dominio y una
 * pantalla pensada para brokers— para escribir cuatro campos. `POST /contacto`
 * es público, así que el portal lo consume directo.
 *
 * Diferencias con el original, todas por el mismo motivo (el portal es anónimo y
 * no tiene sesión):
 * - No hay `AuthService`: allá el email viene precargado con el del usuario
 *   logueado, acá siempre arranca vacío.
 * - Los controles se llaman igual que los campos del DTO (`nombre`, `email`,
 *   `mensaje`), así que el mapeo `campo del backend → control` que allá hace
 *   falta (`BACKEND_A_CONTROL`) es la identidad y se puede omitir.
 * - No se pregunta el tipo de consulta (FP-48): ese desplegable quedó sólo en el
 *   backoffice. El DTO lo sigue exigiendo, así que el valor lo pone
 *   `ContactoService` al armar el POST.
 * - Los errores que el backend no asocia a un campo van al alerta de arriba del
 *   formulario, no a un toast: el portal no tiene pila de toasts.
 * - El header y el footer los pone el shell (`app.ts`), no la pantalla.
 */
@Component({
  selector: 'app-contacto-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, Alerta, InputTexto, Modal],
  templateUrl: './contacto-page.html',
  styleUrl: './contacto-page.scss',
})
export class ContactoPage {
  private readonly contacto = inject(ContactoService);

  readonly form = new FormGroup({
    nombre: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, validadorEmail]),
    mensaje: new FormControl('', [
      Validators.required,
      Validators.maxLength(MENSAJE_MAX),
    ]),
  });

  readonly mensajeMax = MENSAJE_MAX;

  readonly enviando = signal(false);
  /** Error general del envío; los de un campo se muestran bajo el campo. */
  readonly error = signal<string | null>(null);
  /** Envío exitoso: se muestra el modal de confirmación. */
  readonly enviado = signal(false);

  private readonly erroresBackend = new ErroresBackendForm(this.form);

  /**
   * El estado del formulario como signal. Con `OnPush`, `form.valid` leído
   * directo en la plantilla depende de que algo más dispare la detección de
   * cambios; así el botón se habilita y se deshabilita solo.
   */
  private readonly estado = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  readonly puedeEnviar = computed(
    () => this.estado() === 'VALID' && !this.enviando(),
  );

  constructor() {
    // Editar cualquier campo descarta el error del envío anterior (mismo
    // criterio que el backoffice).
    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.error.set(null));
  }

  /**
   * Texto bajo el campo. Aparece recién cuando el campo fue visitado: mientras
   * se tipea, el borde ya avisa que el valor no sirve y el texto sería ruido.
   */
  protected mensajeDeError(nombre: string): string {
    const control = this.form.get(nombre);
    if (!control?.invalid || !control.touched) return '';
    // El motivo por el que el backend rechazó el campo es más específico que el
    // mensaje local, así que tiene prioridad.
    return this.erroresBackend.mensajeDe(nombre) ?? ERROR_LOCAL[nombre] ?? '';
  }

  protected enviar(): void {
    if (!this.puedeEnviar()) return;

    const valores = this.form.getRawValue();
    this.enviando.set(true);
    this.error.set(null);
    // Reintento limpio: se descartan los campos marcados en el intento anterior.
    this.erroresBackend.limpiar();

    this.contacto
      .enviar({
        nombre: valores.nombre ?? '',
        email: valores.email ?? '',
        mensaje: valores.mensaje ?? '',
      })
      .subscribe({
        next: () => {
          this.enviando.set(false);
          this.form.reset();
          this.enviado.set(true);
        },
        error: (err: unknown) => {
          this.enviando.set(false);
          const { mensajes, campos } = parsearErrorBackend(
            err,
            'No pudimos enviar tu mensaje. Volvé a intentarlo en unos minutos.',
          );
          // Con los campos señalados, cada uno queda en rojo con su motivo
          // debajo; arriba va sólo lo que no entró en ningún campo.
          const sobrantes = campos.length
            ? this.erroresBackend.aplicar(campos)
            : mensajes;
          if (sobrantes.length) this.error.set(sobrantes.join(' '));
        },
      });
  }

  protected cerrarModal(): void {
    this.enviado.set(false);
  }
}
